import { generateErrorReport, getSalesTaxFile } from '@commenda/api';
import {
  Job,
  JobEntity,
  JobOperation,
  JobStatus,
} from '@commenda/api/new-sales-tax/job/types';
import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
  Tooltip,
} from '@commenda/kakashi';
import { useState } from 'react';
import { formatDateTime } from '../../../sales-tax/settings-tab/dataIntegration/common/utils';
import {
  JOB_ENTITY_TO_TEXT,
  JOB_OPERATION_TO_TEXT,
  JOB_STATUS_CONFIG,
} from './contant';

interface JobsLogTableProps {
  isOpen: boolean;
  onClose: () => void;
  data: Job[];
  additionalColumns?: string[];
  corporationId: number;
  jobOperation?: JobOperation;
  title?: string;
}

export const JobsLogTable = ({
  isOpen,
  onClose,
  data,
  additionalColumns = [],
  corporationId,
  jobOperation = JobOperation.IMPORT,
  title = 'Transaction Reports',
}: JobsLogTableProps) => {
  const [generatingReportJobId, setGeneratingReportJobId] = useState<
    string | undefined
  >();

  const getReportFile = async (fileId: string) => {
    if (fileId) {
      const fileResponse = await getSalesTaxFile(fileId, corporationId);

      if (fileResponse.success) {
        const fileData = fileResponse.data?.data;
        window.open(fileData.signed_url, '_blank');
      }
    }
  };

  const downloadErrorReport = async (job: Job) => {
    setGeneratingReportJobId(job.id);
    try {
      let fileId = job.error_report_file_id;
      if (!fileId) {
        const response = await generateErrorReport(job.id, corporationId);
        fileId = response.data?.data?.error_report_file_id;
        if (!response.success || !fileId) return;
      }
      await getReportFile(fileId);
    } finally {
      setGeneratingReportJobId(undefined);
    }
  };
  const columnGroups = [
    {
      id: 'id',
      columns: [
        {
          accessorKey: 'id',
          header: 'Task ID',
          title: 'Task ID',
          width: 250,
          cell: ({ row }) => (
            <p className="txt-small-regular text-text-primary truncate">
              {row.getValue('id')}
            </p>
          ),
        },
      ],
    },
    {
      id: 'user',
      columns: [
        {
          accessorKey: 'user',
          header: 'User',
          title: 'User',
          cell: ({ row }) => {
            const user: Job['user'] = row.getValue('user');
            return (
              <p className="txt-small-regular text-text-primary truncate">
                {user?.name ?? '-'}
              </p>
            );
          },
        },
      ],
    },
    {
      id: 'submitted_at',
      columns: [
        {
          accessorKey: 'submitted_at',
          header: 'Submitted At',
          title: 'Submitted At',
          showInSelector: false,
          cell: ({ row }) => {
            const timestamp = Number(row.getValue('submitted_at')) / 1000; // Convert to seconds
            const date = new Date(timestamp);
            const formattedDate = date ? formatDateTime(date) : '-';
            return (
              <p className="txt-small-regular text-text-primary truncate">
                {formattedDate}
              </p>
            );
          },
        },
      ],
    },
    {
      id: 'file',
      columns: [
        {
          accessorKey: 'file_name',
          showInSelector: false,
          header: 'File Name',
          title: 'File Name',
          width: 250,
          cell: ({ row }) => {
            const value = row.getValue('file_name') || '-';

            return (
              <p className="txt-small-regular text-text-primary truncate">
                <Tooltip content={value}>
                  <div className="w-[350px] truncate">{value}</div>
                </Tooltip>
              </p>
            );
          },
        },
      ],
    },
    {
      id: 'entity',
      columns: [
        {
          accessorKey: 'entity',
          header: 'Entity',
          title: 'Entity Type',
          cell: ({ row }) => {
            const entity = row.getValue('entity') as JobEntity;
            return <Badge variant="gray">{JOB_ENTITY_TO_TEXT[entity]}</Badge>;
          },
        },
      ],
    },
    {
      id: 'operation',
      columns: [
        {
          accessorKey: 'operation',
          header: 'Operation',
          title: 'Operation Type',
          cell: ({ row }) => {
            const operation = row.getValue('operation') as JobOperation;
            return (
              <Badge variant="gray">{JOB_OPERATION_TO_TEXT[operation]}</Badge>
            );
          },
        },
      ],
    },
    {
      id: 'status',
      columns: [
        {
          accessorKey: 'status',
          header: 'Status',
          title: 'Status',
          cell: ({ row }) => {
            const status = row.getValue('status') as JobStatus;
            const config = JOB_STATUS_CONFIG[status];
            return (
              <Badge variant={config.variant as any}>{config.label}</Badge>
            );
          },
        },
      ],
    },

    {
      id: 'total_rows',
      columns: [
        {
          accessorKey: 'total_rows',
          header: 'Total Rows',
          title: 'Total Rows',
          cell: ({ row }) => {
            const original = row.original as Job;
            return (
              <div className="flex items-center gap-2">
                <p className="txt-small-regular text-text-primary">
                  {original.total_rows || '-'}
                </p>

                {original?.success_count > 0 && (
                  <Badge variant="success">
                    Success: {original.success_count}
                  </Badge>
                )}
                {original?.failure_count > 0 && (
                  <Badge variant="error">
                    Failed: {original.failure_count}
                  </Badge>
                )}
              </div>
            );
          },
        },
      ],
    },
    {
      id: 'actions',
      sticky: 'right' as const,

      columns: [
        jobOperation === JobOperation.EXPORT && {
          id: 'actions',
          accessorKey: 'actions',
          header: '',
          showInSelector: false,
          cell: ({ row }) => {
            const original = row.original as Job;
            const hasReports = original.export_file_id;

            return (
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasReports}
                onClick={() => getReportFile(original.export_file_id)}
              >
                <Icons.Download01 className="size-4" />
                <span>Download Report</span>
              </Button>
            );
          },
        },
        jobOperation === JobOperation.IMPORT && {
          id: 'actions',
          accessorKey: 'actions',
          header: '',
          showInSelector: false,
          cell: ({ row }) => {
            const original = row.original as Job;
            const hasReportableErrors =
              original.status === JobStatus.COMPLETED &&
              (original.failure_count ?? 0) > 0;
            const isGenerating = generatingReportJobId === original.id;

            return (
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasReportableErrors || isGenerating}
                isLoading={isGenerating}
                onClick={() => downloadErrorReport(original)}
              >
                <Icons.Download01 className="size-4" />
                <span>Download Error Report</span>
              </Button>
            );
          },
        },
      ].filter(Boolean),
    },
  ];

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="bg-white min-w-[700px] max-w-[80vw] flex flex-col max-h-[70vh] p-0 overflow-hidden"
        hideCloseIcon
        preventCloseOnOutsideClick
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="p-6 overflow-y-auto flex-1">
          <DataTable
            columnGroups={columnGroups}
            data={data}
            tableViewConfig={{
              isEnabled: true,
              defaultView: 'regular',
            }}
            rowClassName={() => 'cursor-default hover:bg-primary'}
            columnSelectorConfig={{
              isEnabled: true,
              placeholder: 'Customise columns',
              searchText: 'Search columns',
              notFoundText: 'No columns found',
              selectAllText: 'Show all columns',
              defaultVisibleColumns: [
                ...additionalColumns,
                'id',
                'entity',
                'submitted_at',
                'operation',
                'status',
                'file_name',
                'total_rows',
                'actions',
              ],
            }}
            searchConfig={{
              isEnabled: true,
              placeholder: 'Search by file name or ID',
              columnsToEnableSearchFor: ['file_name', 'id'],
            }}
            emptyStateConfig={{
              title: 'No jobs found',
              description: 'There are no import jobs to display at this time.',
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} size="sm" type="button">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
